
import { useState } from 'react';
import { toast } from 'sonner';
import { MapsService } from '@/lib/services/mapsService';
import { validateGoogleMapsUrl } from '@/lib/services/maps';
import { GoogleMapsApiResponse } from '@/types/api';
import { logger } from '@/lib/utils/logger';

const componentLogger = logger.forComponent('MapsLinkProcessor');

export const useMapsLinkProcessor = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');

  const processLink = async (
    link: string,
    onSuccess: (data: GoogleMapsApiResponse) => void,
    onError: () => void
  ) => {
    setError('');
    
    if (!validateGoogleMapsUrl(link)) {
      const errorMsg = 'Please enter a valid Google Maps link (you can get this by clicking "Share" in Google Maps)';
      setError(errorMsg);
      toast.error("Invalid Google Maps link");
      return;
    }

    setIsProcessing(true);
    
    // Show different loading messages based on processing time
    const loadingToast = toast.loading("Analyzing location...", {
      description: "Extracting place information from Google Maps"
    });
    
    // Update loading message after 3 seconds
    const timeoutId = setTimeout(() => {
      toast.loading("Still processing...", {
        id: loadingToast,
        description: "This is taking longer than usual, please wait"
      });
    }, 3000);
    
    try {
      const data = await MapsService.processGoogleMapsLink(link);
      
      clearTimeout(timeoutId);
      toast.dismiss(loadingToast);
      
      componentLogger.info('Maps processing successful', { placeName: data.name });
      toast.success("Location details retrieved successfully!", {
        description: `Found: ${data.name || 'Unknown place'}`
      });
      
      onSuccess(data as GoogleMapsApiResponse);
    } catch (error) {
      clearTimeout(timeoutId);
      toast.dismiss(loadingToast);
      
      console.error("Error processing Google Maps link:", error);
      componentLogger.error("Error processing Google Maps link", {}, error as Error);
      
      let errorMessage = error instanceof Error ? error.message : 'Failed to process the link';
      let toastDescription = '';
      
      // Enhanced error handling with specific user guidance
      if (errorMessage.includes('Invalid Google Maps URL') || errorMessage.includes('valid Google Maps link')) {
        errorMessage = 'Please provide a valid Google Maps link';
        toastDescription = 'Use the "Share" button in Google Maps to get the correct link format';
      } else if (errorMessage.includes('Could not find place') || errorMessage.includes('No places found')) {
        errorMessage = 'Could not find the place in Google Maps';
        toastDescription = 'Make sure the link points to a specific business or location';
      } else if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
        errorMessage = 'Request timed out - please try again';
        toastDescription = 'The processing took too long, please retry with the same link';
      } else if (errorMessage.includes('API') || errorMessage.includes('configuration')) {
        errorMessage = 'Service temporarily unavailable';
        toastDescription = 'Please try again in a few moments or fill the form manually';
      } else if (errorMessage.includes('permissions') || errorMessage.includes('access denied')) {
        errorMessage = 'Unable to access place information';
        toastDescription = 'Please try a different location or fill the form manually';
      } else {
        // Generic error - provide helpful fallback message
        errorMessage = 'Unable to process this Google Maps link';
        toastDescription = 'Try copying the link directly from the "Share" button in Google Maps';
      }
      
      setError(errorMessage);
      toast.error(errorMessage, {
        description: toastDescription,
        duration: 6000
      });
      
      onError();
    } finally {
      setIsProcessing(false);
    }
  };

  const resetError = () => setError('');

  return {
    isProcessing,
    error,
    processLink,
    resetError
  };
};
